import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import CellDate from 'src/registry/components/table/cells/CellDate';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { customersMessages } from 'src/features/customers/utils/messages';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import { billingRunsMessages } from 'src/features/billingRuns/utils/messages';
import { BILLING_RUN_TYPE_BADGE_OPTIONS } from 'src/features/billingRuns/utils/constants';
import { BillingAttempt } from 'src/features/billingAttempts/utils/types';
import { useListBillingAttempts } from 'src/features/billingAttempts/api/useListBillingAttempts';
import { billingAttemptsMessages } from 'src/features/billingAttempts/utils/messages';
import {
    BILLING_ATTEMPT_STATUS_BADGE_OPTIONS,
    ERROR_CODE_BADGE_OPTIONS,
    ERROR_CODE_MAP,
} from 'src/features/billingAttempts/utils/constants';
import CellBillingAttemptActions from 'src/features/billingAttempts/components/tables/CellBillingAttemptActions';

type BillingAttemptsTableProps = {
    /** Set by the billing run dashboard to scope the list to one run. */
    billingRunId?: string;
};

const BillingAttemptsTable = ({ billingRunId }: BillingAttemptsTableProps) => {
    const { formatMessage } = useIntl();

    const permissionAccessSubscriptions = useHasPermissions([PERMISSION.ACCESS_SUBSCRIPTIONS]);
    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'error_detail' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'created_at', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: billingAttempts,
        isError,
        isLoading,
    } = useListBillingAttempts({
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...(billingRunId && { billing_run: [billingRunId] }),
    });

    const columnHelper = createColumnHelper<BillingAttempt>();

    // No self-link column: attempts have no dashboard route.
    const columns = [
        columnHelper.accessor('created_at', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: CellDatetime,
            meta: { title: formatMessage(generalMessages.created) },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(commonMessages.type),
                badgeOptions: BILLING_RUN_TYPE_BADGE_OPTIONS,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(generalMessages.status),
                badgeOptions: BILLING_ATTEMPT_STATUS_BADGE_OPTIONS,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        // Cross-link, so it takes the target's ACCESS_* permission, never its MANAGE_*.
        columnHelper.accessor('subscription_item.subscription.reference', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const subscription = ctx.row.original.subscription_item?.subscription;
                if (!subscription) return '-';
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/subscriptions/${subscription.id}`}
                        hasPermissions={permissionAccessSubscriptions}
                    />
                );
            },
            meta: { title: formatMessage(subscriptionsMessages.refNumber) },
        }),
        columnHelper.accessor('subscription_item.service_plan_item.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => row.original.subscription_item?.service_plan_item?.name ?? '-',
            meta: { title: formatMessage(servicePlanItemsMessages.item) },
        }),
        columnHelper.accessor('customer.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const customer = ctx.row.original.customer;
                if (!customer) return '-';
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/customers/${customer.id}`}
                        hasPermissions={permissionAccessCustomers}
                    />
                );
            },
            meta: { title: formatMessage(customersMessages.customer) },
        }),
        columnHelper.accessor('error_code', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            // Operators get a sentence, never a raw slug.
            cell: ({ row }) => {
                const code = row.original.error_code;
                if (!code) return '-';
                return <span className="text-sm">{formatMessage(ERROR_CODE_MAP[code])}</span>;
            },
            meta: {
                title: formatMessage(billingAttemptsMessages.errorCode),
                badgeOptions: ERROR_CODE_BADGE_OPTIONS,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('error_detail', {
            header: DataTableColumnHeader,
            enableHiding: true,
            // May be a traceback: truncate, full text in the tooltip, never parsed.
            cell: ({ row }) => {
                const detail = row.original.error_detail;
                if (!detail) return '-';
                return (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="block max-w-[280px] truncate font-mono text-xs">{detail}</span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[480px] break-words whitespace-pre-wrap">
                                {detail}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                );
            },
            meta: { title: formatMessage(billingRunsMessages.errorDetail) },
        }),
        columnHelper.accessor('billing_period_start', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellDate,
            meta: {
                title: formatMessage(billingAttemptsMessages.billingPeriodStart),
                inputType: INPUT_TYPE.DATE,
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellBillingAttemptActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<BillingAttempt, unknown>
            columns={columns}
            data={billingAttempts}
            semantics={{
                title: formatMessage(billingAttemptsMessages.billingAttempts),
                entryName: formatMessage(billingAttemptsMessages.billingAttempt).toLowerCase(),
                entryNames: formatMessage(billingAttemptsMessages.billingAttempts).toLowerCase(),
                placeholder: formatMessage(billingAttemptsMessages.billingAttempt).toLowerCase(),
            }}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
};

export default BillingAttemptsTable;
